import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import {
  addExcludedSender,
  listExcludedSenders,
  removeExcludedSender,
  type ExcludedSender,
} from "../api";

// ── Dialog mittenti esclusi (#6 legacy parity) ─────────────────────────
export function ExcludedSendersDialog({
  accountId,
  accountLabel,
  onClose,
}: {
  accountId: number;
  accountLabel: string;
  onClose: () => void;
}) {
  const [items, setItems] = useState<ExcludedSender[]>([]);
  const [newPattern, setNewPattern] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAdd, setPendingAdd] = useState(false);
  // Simmetrico a ``pendingAdd``, ma per riga: senza, un secondo click su un
  // backend lento manda una seconda DELETE sullo stesso id → 404 mostrato come
  // errore su una rimozione in realtà riuscita.
  const [pendingRemove, setPendingRemove] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    void listExcludedSenders(accountId)
      .then((rows) => {
        if (!cancelled) {
          setItems(rows);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(prettifyApiError(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  async function handleAdd() {
    const pattern = newPattern.trim();
    if (!pattern) return;
    setPendingAdd(true);
    try {
      const row = await addExcludedSender(accountId, pattern);
      setItems((prev) => (prev.some((p) => p.id === row.id) ? prev : [row, ...prev]));
      setNewPattern("");
      setError(null);
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setPendingAdd(false);
    }
  }

  async function handleRemove(id: number) {
    if (pendingRemove !== null) return;
    setPendingRemove(id);
    try {
      await removeExcludedSender(id);
      setItems((prev) => prev.filter((p) => p.id !== id));
      setError(null);
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setPendingRemove(null);
    }
  }

  // Audit 2026-05-29: ESC chiude il dialog. Prima ``onKeyDown`` era sul div
  // root (non focalizzato) → l'evento non scattava mai. Listener window-level
  // con cleanup (stesso pattern di FornitoriPage CredentialsDialog).
  useEffect(() => {
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="excluded-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-line bg-card shadow-cardOpen">
        <header className="flex items-start justify-between border-b border-line px-5 py-4">
          <div>
            <h2 id="excluded-dialog-title" className="font-serif text-[17px] font-medium text-ink">
              Mittenti esclusi
            </h2>
            <p className="mt-0.5 font-mono text-[10.5px] text-ink-3">{accountLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="-mr-1 rounded-sm p-1 text-ink-3 hover:bg-paper hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        <div className="px-5 py-4">
          <p className="mb-3 text-[12px] text-ink-3">
            Aggiungi un pattern (es.{" "}
            <code className="rounded bg-paper px-1 font-mono">@spam.com</code> o un customer_id). I
            thread che matchano sono nascosti dalla lista.
          </p>
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              value={newPattern}
              onChange={(event) => setNewPattern(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void handleAdd();
              }}
              placeholder="Pattern (substring case-insensitive)"
              className="flex-1 rounded-md border border-line bg-card px-2.5 py-1.5 text-[12.5px] outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={!newPattern.trim() || pendingAdd}
              className="rounded-md border border-ink bg-ink px-3 py-1.5 text-[12px] font-medium text-on-ink disabled:opacity-50"
            >
              {pendingAdd ? "Aggiungo…" : "Aggiungi"}
            </button>
          </div>
          <ErrorBanner message={error} className="mb-2" />
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <p className="py-4 text-center text-[12px] italic text-ink-3">Caricamento…</p>
            ) : items.length === 0 ? (
              <p className="py-4 text-center text-[12px] italic text-ink-3">
                Nessun mittente escluso per questo account.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {items.map((item) => (
                  <li key={item.id} className="flex items-center gap-2 py-2 text-[12.5px]">
                    <span className="flex-1 font-mono text-ink-2">{item.pattern}</span>
                    <button
                      type="button"
                      onClick={() => void handleRemove(item.id)}
                      disabled={pendingRemove !== null}
                      aria-busy={pendingRemove === item.id}
                      aria-label={`Rimuovi pattern ${item.pattern}`}
                      className="rounded px-1.5 py-0.5 text-[11px] text-ink-3 hover:bg-red-soft hover:text-ink disabled:opacity-50"
                    >
                      {pendingRemove === item.id ? "Rimuovo…" : "Rimuovi"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
